import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActionConfirmDirective } from '@app/+authenticated/shared/directives/action-confirm.directive';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { PermissionCheckPipe } from '@app/pipes/permission-flag.pipe';
import { CorrectionService } from '@app/reducers/orm/correction/correction.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconDelete2Regular } from '@sb/mgc-icons/mgcIcon-delete_2_regular.icon';
import { TooltipDirective } from '@sb/tooltip';
import { BadgeIconComponent } from '@sb/ui/badge-icon';
import { IconButtonComponent } from '@sb/ui/button';
import { TooltipDividerDirective } from '@sb/ui/divider';
import { IconComponent, IconRegistry, type Icon } from '@sb/ui/icon';

import type { EmployeeOverviewData } from '../../../../employee-absence-overview.model';
import { CorrectionType, type PartialBalanceItem } from '../time-off-partial-balance.model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'time-off-partial-balance-item',
  templateUrl: './time-off-partial-balance-item.component.html',
  imports: [
    CommonModule,
    IconComponent,
    TooltipDirective,
    TranslatePipe,
    BadgeIconComponent,
    LocaleDatePipe,
    ActionConfirmDirective,
    IconButtonComponent,
    TooltipDividerDirective,
  ],
  providers: [LocaleDatePipe, PermissionCheckPipe],
})
export class TimeOffPartialBalanceItemComponent implements OnChanges {
  private permissionCheck = inject(PermissionCheckPipe);
  private correctionService = inject(CorrectionService);
  private translate = inject(TranslateService);

  @Input()
  item: PartialBalanceItem;

  @Input()
  employee: EmployeeOverviewData;

  isActive = false;
  correctionTypeMap: Record<CorrectionType, { icon: Icon; label: string }> = {
    [CorrectionType.CORRECTION]: { icon: 'exchange-vertical', label: this.translate.instant('Correction') },
    [CorrectionType.PAYOUT]: { icon: 'euro-circle', label: this.translate.instant('Payout') },
    [CorrectionType.TRANSFER]: { icon: 'exchange-horizontal', label: this.translate.instant('Transfer') },
    [CorrectionType.REACTIVATION]: { icon: 'future', label: this.translate.instant('Reactivation') },
  };

  canDelete = false;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconDelete2Regular]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['employee']) {
      this.canDelete = this.permissionCheck.transform(
        'Adjust time off balances',
        this.employee.id,
        this.employee.departments,
      );
    }
  }

  deleteCorrection(id: string) {
    void this.correctionService.remove(id).subscribe();
  }
}
